"use client";

import React, { useState } from "react";
import { ChevronDown, ChevronUp, ArrowRight, CheckCircle2, Clock, Sparkles, Check, ArrowDown } from "lucide-react";
import { ProcedureSectionData } from "@/lib/procedure-sections/types";
import { SafeHtml } from "@/components/shared/SafeHtml";

function ColoredTitle({ text }: { text?: string }) {
  if (!text) return null;
  const words = text.split(" ");
  if (words.length <= 1) return <span>{text}</span>;
  
  let orangeCount = 1;
  if (words.length >= 6) orangeCount = 3;
  else if (words.length >= 4) orangeCount = 2;

  const firstPart = words.slice(0, words.length - orangeCount).join(" ");
  const lastPart = words.slice(words.length - orangeCount).join(" ");

  return (
    <>
      <span className="text-brand-black">{firstPart} </span>
      <span className="text-brand-orange">{lastPart}</span>
    </>
  );
}

export function ProcessStepsSection({ section, index }: { section: ProcedureSectionData; index: number }) {
  const variant = section.layoutVariant || "steps_numbered_cards";
  const [openStep, setOpenStep] = useState<number | null>(0);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8 md:mb-12 max-w-3xl">
        {section.badge && (
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-peach/40 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-brand-orange-deep">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-orange animate-pulse" />
            <span>{section.badge}</span>
          </div>
        )}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-black leading-tight">
          <ColoredTitle text={section.title} />
        </h2>
        {section.subtitle && (
          <p className="mt-3 text-lg text-brand-gray leading-relaxed">{section.subtitle}</p>
        )}
        <div className="mt-4 h-1 w-24 rounded-full bg-brand-orange" />
      </div>

      {/* Optional intro content */}
      {section.contentHtml && (
        <div className="mb-6 md:mb-10 prose prose-lg max-w-none text-brand-gray font-normal leading-relaxed text-base sm:text-lg cms-content">
          <SafeHtml html={section.contentHtml} />
        </div>
      )}

      {/* Steps Variants */}
      {section.items && section.items.length > 0 && (
        <>
          {/* 1. INTERACTIVE TABBED STAGES VARIANT */}
          {variant === "steps_tabbed_stages" && (
            <div className="my-6 md:my-10 max-w-4xl mx-auto space-y-8">
              {/* Tabs Navigator */}
              <div className="flex flex-wrap items-center justify-center gap-2.5 rounded-2xl bg-[#F6F5F2] p-2.5 border border-brand-gray/15">
                {section.items.map((item, itemIdx) => {
                  const isActive = (openStep ?? 0) === itemIdx;
                  return (
                    <button
                      key={itemIdx}
                      type="button"
                      onClick={() => setOpenStep(itemIdx)}
                      className={`flex items-center gap-2 rounded-xl px-5 py-3 text-xs sm:text-sm font-bold transition-all duration-300 ${
                        isActive
                          ? "bg-brand-orange text-white shadow-lg shadow-brand-orange/25 scale-102"
                          : "text-brand-gray bg-white/80 hover:bg-white hover:text-brand-black shadow-xs"
                      }`}
                    >
                      <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-mono ${
                        isActive ? "bg-white text-brand-orange font-bold" : "bg-brand-gray-light text-brand-gray"
                      }`}>
                        {itemIdx + 1}
                      </span>
                      <span>{item.title || `Stage ${itemIdx + 1}`}</span>
                    </button>
                  );
                })}
              </div>

              {/* Active Stage Detail Card */}
              {section.items[openStep ?? 0] && (
                <div className="rounded-3xl border border-brand-orange/30 bg-gradient-to-br from-[#FFF9F5] via-white to-brand-peach/15 p-8 sm:p-12 shadow-xl animate-in fade-in zoom-in-95 duration-300">
                  <div className="flex items-center justify-between border-b border-brand-gray/10 pb-4 mb-6">
                    <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-deep">
                      Clinical Stage 0{(openStep ?? 0) + 1} of 0{section.items.length}
                    </span>
                    <span className="rounded-full bg-brand-orange px-3.5 py-1 text-xs font-bold text-white shadow-xs">
                      Active Step
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-brand-black mb-4">
                    {section.items[openStep ?? 0].title}
                  </h3>
                  {section.items[openStep ?? 0].description && (
                    <div
                      className="text-base sm:text-lg text-brand-gray leading-relaxed cms-content"
                      dangerouslySetInnerHTML={{ __html: section.items[openStep ?? 0].description }}
                    />
                  )}
                  <div className="mt-8 pt-6 border-t border-brand-orange/15 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-bold text-brand-orange uppercase tracking-wider">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>Standardized AIIMS/ISHRS Safety Protocol</span>
                    </div>
                    {(openStep ?? 0) < section.items.length - 1 && (
                      <button
                        type="button"
                        onClick={() => setOpenStep((openStep ?? 0) + 1)}
                        className="inline-flex items-center gap-1 text-xs font-bold text-brand-orange-deep hover:underline"
                      >
                        <span>Next Step</span>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 2. CONNECTED VERTICAL TIMELINE VARIANT */}
          {variant === "steps_vertical_timeline" && (
            <div className="relative pl-8 sm:pl-16 max-w-4xl mx-auto space-y-12 before:absolute before:left-4 sm:before:left-8 before:top-6 before:bottom-6 before:w-0.5 before:bg-gradient-to-b before:from-brand-orange before:via-brand-orange/40 before:to-transparent">
              {section.items.map((item, itemIdx) => (
                <div key={itemIdx} className="relative group">
                  {/* Glowing Milestone Node */}
                  <div className="absolute -left-[45px] sm:-left-[77px] top-4 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white shadow-xl shadow-brand-orange/20 text-xs sm:text-sm font-bold font-mono z-10 transition-transform duration-300 group-hover:scale-110">
                    0{itemIdx + 1}
                  </div>
                  
                  {/* Decorative pointer arrow to connect node to card */}
                  <div className="absolute -left-2 top-[32px] w-4 h-4 bg-white border-l border-t border-brand-gray/15 -rotate-45 transform -translate-y-1/2 z-0 group-hover:border-brand-orange/40 transition-colors duration-300" />
                  
                  <div className="relative z-10 rounded-3xl border border-brand-gray/15 bg-white p-7 sm:p-10 shadow-lg shadow-brand-gray/5 transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/40 hover:shadow-2xl hover:shadow-brand-orange/10 overflow-hidden">
                    {/* Background glow effect on hover */}
                    <div className="absolute -right-20 -top-20 w-40 h-40 bg-brand-peach/40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-brand-orange-deep bg-brand-orange/10 px-3 py-1 rounded-full">
                          Phase 0{itemIdx + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-brand-black mb-3">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-base sm:text-lg text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 3. INTERACTIVE ACCORDION STEPS */}
          {variant === "steps_accordion" && (
            <div className="space-y-4 max-w-4xl mx-auto">
              {section.items.map((item, itemIdx) => {
                const isOpen = openStep === itemIdx;
                return (
                  <div
                    key={itemIdx}
                    className={`overflow-hidden rounded-3xl border transition-all duration-300 ${
                      isOpen 
                        ? "border-brand-orange bg-white shadow-xl ring-2 ring-brand-orange/10" 
                        : "border-brand-gray/15 bg-white shadow-xs hover:border-brand-gray/30"
                    }`}
                  >
                    <button
                      onClick={() => setOpenStep(isOpen ? null : itemIdx)}
                      className="flex w-full items-center justify-between gap-4 p-6 text-left transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-bold text-sm font-mono transition-colors ${
                          isOpen ? "bg-brand-orange text-white shadow-md shadow-brand-orange/30" : "bg-brand-peach/40 text-brand-orange"
                        }`}>
                          0{itemIdx + 1}
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-brand-black">
                          {item.title}
                        </h3>
                      </div>
                      <div className={`shrink-0 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${
                        isOpen ? "bg-brand-orange text-white" : "bg-brand-gray-light text-brand-gray"
                      }`}>
                        {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </button>
                    {isOpen && item.description && (
                      <div className="border-t border-brand-gray/10 bg-[#FAF9F6] p-7 text-base text-brand-gray leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: item.description }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 4. HORIZONTAL ROADMAP WITH ARROWS */}
          {variant === "steps_horizontal_roadmap" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, itemIdx) => {
                const isNotLast = itemIdx < (section.items?.length || 0) - 1;
                return (
                  <div
                    key={itemIdx}
                    className="relative flex flex-col justify-between rounded-3xl border border-brand-gray/15 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-orange/40 hover:shadow-xl"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-peach/40 text-brand-orange font-mono text-base font-bold">
                          0{itemIdx + 1}
                        </div>
                        {isNotLast && (
                          <div className="hidden lg:flex items-center text-brand-orange">
                            <ArrowRight className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      <h3 className="text-xl font-bold text-brand-black mb-3">
                        {item.title}
                      </h3>
                      {item.description && (
                        <div 
                          className="text-sm sm:text-base text-brand-gray leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: item.description }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 5. ALTERNATING ZIG-ZAG PATHWAY */}
          {variant === "steps_zigzag_journey" && (
            <div className="relative my-10 md:my-20 max-w-7xl mx-auto space-y-16 md:space-y-32">
              {/* Central glowing timeline track */}
              <div className="hidden md:block absolute left-1/2 top-10 bottom-10 w-[1px] -translate-x-1/2 bg-gradient-to-b from-transparent via-brand-orange/30 to-transparent z-0" />
              
              {section.items.map((item, itemIdx) => {
                const isEven = itemIdx % 2 !== 0;
                return (
                  <div
                    key={itemIdx}
                    className={`relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12 lg:gap-24 group ${
                      isEven ? "md:flex-row-reverse" : ""
                    }`}
                  >
                    {/* Editorial Content Side */}
                    <div className={`w-full md:w-1/2 flex flex-col ${isEven ? 'md:items-end md:text-right' : 'md:items-start md:text-left'} text-center md:text-left`}>
                      {/* Premium Watermark & Step Badge */}
                      <div className="relative w-full max-w-lg mb-6">
                        <span className={`absolute -top-16 ${isEven ? 'md:-right-8' : 'md:-left-8'} text-[120px] font-black text-brand-orange/5 select-none tracking-tighter pointer-events-none group-hover:text-brand-orange/10 transition-colors duration-700`}>
                          0{itemIdx + 1}
                        </span>
                        
                        <div className="relative z-10 inline-flex items-center gap-3 mb-6">
                          <span className="flex items-center justify-center h-8 w-8 rounded-full bg-brand-orange text-white text-xs font-bold shadow-lg shadow-brand-orange/40 group-hover:scale-110 transition-transform duration-500">
                            {itemIdx + 1}
                          </span>
                          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-orange-deep">
                            Treatment Phase
                          </span>
                        </div>
                        
                        <h3 className="relative z-10 text-3xl md:text-4xl lg:text-5xl font-bold text-brand-black leading-tight mb-6">
                          {item.title}
                        </h3>
                        
                        <div className={`h-1 w-16 bg-gradient-to-r from-brand-orange to-brand-peach mb-8 mx-auto ${isEven ? 'md:ml-auto md:mr-0' : 'md:ml-0 md:mr-auto'}`} />
                        
                        {item.description && (
                          <div
                            className="relative z-10 text-lg md:text-xl text-brand-gray leading-relaxed font-light"
                            dangerouslySetInnerHTML={{ __html: item.description }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Central Node Indicator */}
                    <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-white border-[4px] border-brand-orange shadow-[0_0_20px_rgba(240,113,103,0.6)] z-20 group-hover:bg-brand-orange group-hover:border-white transition-all duration-500 group-hover:scale-125" />

                    {/* Luxury Image Showcase */}
                    <div className="w-full md:w-1/2 relative perspective-1000">
                      {/* Decorative backdrop block */}
                      <div className={`absolute inset-0 bg-gradient-to-br from-brand-peach/40 to-brand-orange/10 rounded-3xl transform ${isEven ? 'rotate-3 translate-x-4' : '-rotate-3 -translate-x-4'} transition-transform duration-700 group-hover:rotate-0 group-hover:translate-x-0`} />
                      
                      {item.image ? (
                        <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl z-10 group-hover:-translate-y-2 transition-all duration-700">
                          <img 
                            src={item.image} 
                            alt={item.title} 
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-1000 ease-out" 
                          />
                          {/* Elegant inset border & overlay */}
                          <div className="absolute inset-0 ring-1 ring-inset ring-white/20 rounded-3xl" />
                          <div className="absolute inset-0 bg-gradient-to-t from-brand-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                        </div>
                      ) : (
                        <div className="relative w-full aspect-[4/3] rounded-3xl bg-[#FAF9F6] flex items-center justify-center border border-brand-gray/10 z-10">
                          <span className="text-brand-gray font-mono text-sm tracking-widest uppercase">Visual Pending</span>
                        </div>
                      )}
                      
                      {/* Floating glowing orb */}
                      <div className={`absolute ${isEven ? '-left-12 top-1/2' : '-right-12 top-1/2'} w-32 h-32 bg-brand-orange/30 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none z-0`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 6. NUMBERED LUXURY HERO CARDS (DEFAULT) */}
          {variant === "steps_numbered_cards" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((item, itemIdx) => (
                <div
                  key={itemIdx}
                  className="group relative overflow-hidden rounded-3xl border border-brand-gray/15 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-orange/40 hover:shadow-xl flex flex-col justify-between"
                >
                  {/* Watermark Number */}
                  <span className="absolute -right-2 -bottom-4 text-7xl font-black text-brand-gray-light/60 select-none group-hover:text-brand-orange/10 transition-colors pointer-events-none font-mono">
                    0{itemIdx + 1}
                  </span>

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-mono text-xs font-bold text-brand-orange-deep bg-brand-peach/40 px-3 py-1 rounded-full">
                        Step 0{itemIdx + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-brand-black mb-3 group-hover:text-brand-orange-deep transition-colors">
                      {item.title}
                    </h3>
                    {item.description && (
                      <div 
                        className="text-base text-brand-gray leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.description }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
